import { ChangeEvent, FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Buttons, Form, FormElements, H1 } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { Options } from '../../../components/Dropdown/Dropdown.types';
import Input from '../../../components/Input/Input';
import { OPTIONS_PLACEHOLDER } from '../../../constants/Dropdown';
import { useManInvGroups } from '../../../services/manInv/manInv.service';
import { ManInvGroup } from '../../../services/manInv/manInv.types';
import {
  fetchSourceStores,
  useAllStores, useOnBoardNewStore,
} from '../../../services/storeControl/storeControl.service';
import {
  OnBoardStoreFormValues,
  OwnsMenuValues,
  StoreControlResponse
} from '../../../services/storeControl/storeControl.types';
import {
  useGenericErrorMessage,
} from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import {
  CopyMenuWrapper,
  DropdownWrapper,
  HorizontalLine,
  InputButtonWrapper,
  InputWrapper,
  InputWrapperThree,
  InputWrapperTwo,
} from '../StoreRecord.styles';
import { WEB_MENU, WebMenuKeys } from '../../../constants/ApiEnums';
import Autocomplete from '../../../components/Autocomplete/Autocomplete';
import { fetchMenusInMarket } from '../../../services/menu/menu.service';
import { InputActionMeta } from 'react-select';
import AutocompleteInputButton from '../../../components/AutocompleteInputButton/AutocompleteInputButton';
import { MenuTreeResponse } from '../../../services/menu/menu.types';

const AddStoreRecord: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const storeInputRef = useRef<any>(null);

  // Focus the Store # field on component mount for accessibility
  useEffect(() => {
    if (storeInputRef.current) {
      storeInputRef.current.focus();
    }
  }, []);

  const [selectOptions, setSelectOptions] = useState<Options[]>([]);
  const [storesToOnBoard, setStoresToOnBoard] = useState<StoreControlResponse[]>([]);
  const [sourceMenuOptions, setSourceMenuOptions] = useState<Options[]>([]);
  const [storeNoMatch, setStoreNoMatch] = useState(false); // toggle success text for inputted storeNo
  const [manInvGroupOptions, setManInvGroupOptions] = useState<Options[]>([]);
  const todays_date = new Date();

  const [formValues, setFormValues] = useState<OnBoardStoreFormValues>({
    storeNo: '',
    // mxref: '', // TODO: MxRef covered in future story
    manInvGroup: OPTIONS_PLACEHOLDER,
    webMenu: OPTIONS_PLACEHOLDER,
    couponGroupId:'',
    sourceStoreNo: undefined,
    sourceMenu: undefined,
    effectiveDate: '',
    division: '',
    market: '',
    concept: ''
  });

  const { data: manInvGroupsData } = useManInvGroups(true);
  const { mutate, isError, isLoading, error } = useOnBoardNewStore();

  const allManInvGroups = manInvGroupsData?.data || [];

  useGenericErrorMessage([{ isError, error }]);
  const {data: allStores, isLoading: allStoresIsLoading} = useAllStores()


  useEffect(() => {
    if (allManInvGroups) {
      setManInvGroupOptions(
        allManInvGroups
          .sort((manInvOne, manInvTwo) => {
            return manInvOne?.id - manInvTwo?.id;
          })  
          .map((manInvGroup: ManInvGroup) => {
            return { value: manInvGroup?.id?.toString(), label: manInvGroup?.description };
          })
      );
    }
  }, [manInvGroupsData]);

  const handleSearchValueChange = (value: any) => {
    const {value: newStoreNo} = value;
    setFormValues({...formValues, 
      storeNo: newStoreNo,
      concept:'',
      division: '',
      dma: '',
      market: ''
    
    })
  }

  const handleEffectiveDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleSelectManInv = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };
  const handleSelectWebMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  }

  const handleSelectMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  }

  const handleStoreToOnBoard = () => {
    const storeIndex = storesToOnBoard.findIndex(value => value.storeNo === formValues.storeNo);
    if (storeIndex !== -1) {
      const storeFound = storesToOnBoard.at(storeIndex);
      setFormValues({
        ...formValues,
        storeNo: storeFound?.storeNo,
        manInvGroup: '1',
        webMenu: 'WEB-FR',
        couponGroupId: storeFound?.couponGroupId,
        market: storeFound?.marketNo,
        division: storeFound?.division,
        concept: storeFound?.conceptType,
        dma: storeFound?.dma,
      });
    }
    setStoreNoMatch(storeIndex !== -1);
  }

  const handleSearch = (event: MouseEvent) => {
    event.preventDefault();
    handleStoreToOnBoard()
  };


  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    mutate(
      {
        storeNo: formValues.storeNo || '',
        maninvGroupId: Number(formValues.manInvGroup),
        webMenu: formValues?.webMenu || '',
        sourceStoreNo: formValues.sourceStoreNo || '',
        effectiveDate: formValues?.effectiveDate || '',
        sourceMenuStatus: formValues?.sourceMenu,
        webPriceZone: formValues?.webMenu === 'WEB-FR' ? 1 : 0,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('store-controls');
          navigateWithToast('/storecontrol', {
            label: 'Store record has been added.',
            id: 'storeRecordUpdateSuccess',
          });
        },
      }   
    );
  };

  const handleInputChange = (value: string, action: InputActionMeta) => {
    if(action.action === 'input-change' && value){
      fetchSourceStores(
        { storeNo: value },
        (response) => {
          const incomingOptions = response?.data.map((item: OwnsMenuValues) => {
            return {value: item.storeNo, label: item.storeNo};
          });
          setSelectOptions(incomingOptions || []);
        }
      );

    }    
  };

  useEffect(()=> {
    if (allStores) {
      setStoresToOnBoard(allStores.data);
    }
  }, [storesToOnBoard, allStores]);

 const handleSourceStoreNumber = (value: any) => {
  setFormValues({ ...formValues, 'sourceStoreNo': value?.value });
  fetchMenusInMarket(
    { storeNo: value?.value },
     (response) => {
       const incomingOptions = response?.data.map((item: MenuTreeResponse) => {
         let effectiveDate = item.effectiveDate ? item.effectiveDate.slice(0, 10) : '';
         let effectiveDateFormatted = effectiveDate.substring(5, 7).replace(/^0/, '') + '/' + effectiveDate.substring(8).replace(/^0/, '');

         let label = `${item.menuNo} | ${item.description} | ${item.statusNo === 0 ? 'Active' : 'Pending'} | ${ effectiveDateFormatted }`;
         let value = item.statusNo;

         return {value, label};
       });
       setSourceMenuOptions(incomingOptions || []);
     }
   );
 }

 const checkFormFields = () => {
   return formValues.sourceStoreNo !== undefined && formValues.sourceMenu !== undefined && formValues.effectiveDate !== '' && formValues.market !== '';
 }

  return (
    <>
      <Header breadcrumb={{ label: 'Back to Table', to: '/storecontrol' }} title='Store Control' />
      <Form>
        <FormElements>
          <H1>Add New Store Record</H1>
          <H2>Market Info</H2>
          <InputWrapper>            <InputButtonWrapper>
            <AutocompleteInputButton
             disabled={false} 
             label='Store #'
             id='storeNoContainer'
             options={allStores?.data.map((object: StoreControlResponse) => ({value: object.storeNo, label: object.storeNo})) || []}
            // onInputChange={handleSearchInputChange}
             onChange={handleSearchValueChange}
             isLoading={allStoresIsLoading}
             ref={storeInputRef}
             buttonProps={{
              label: 'Search',
              onClick: handleSearch,
            }}
            />
            </InputButtonWrapper>
            <InputWrapperTwo>
              <Input
                label='Concept'
                disabled
                id='concept'
                value={formValues.concept}
              />
              <Input
                label='Division'
                disabled
                id='division'
                value={formValues.division}
              />
            </InputWrapperTwo>
            <InputWrapperThree>
            <Input label='DMA' disabled id='dma' value={formValues.dma} />
              <Input
                label='Market'
                disabled
                id='market'
                value={formValues.market}
              />
            </InputWrapperThree>
          </InputWrapper>
          <HorizontalLine />
          <H2>Store Info</H2>
          {storeNoMatch ? (
            <DropdownWrapper>
              {/* <Dropdown label='MxRef' id='default' disabled /> */}
              <Dropdown
                label='Mandatory Inventory Group'
                id='manInvGroup'
                value={formValues?.manInvGroup}
                options={manInvGroupOptions}
                onChange={handleSelectManInv}
                disabled={false}
              />
              <Dropdown
              label='WebMenu'
              id='webMenu'
              value={formValues?.webMenu}
              options={Object.keys(WEB_MENU).map((key: any) => {
                return { value: key, label: `${WEB_MENU[key as WebMenuKeys]}` };
              })}
              onChange={handleSelectWebMenu}
              disabled={false}
            />
            </DropdownWrapper>
          ) : (
            <DropdownWrapper>
              {/* <Dropdown label='MxRef' id='default' disabled /> */}
              <Dropdown
                label='Mandatory Inventory Group'
                id='manInvGroup'
                value={formValues.manInvGroup ? formValues.manInvGroup : 'NATION'}
                options={manInvGroupOptions}
                onChange={handleSelectManInv}
                disabled
              />
              <Dropdown
              label='WebMenu'
              id='webMenu'
              value={formValues?.webMenu ? formValues.webMenu : 'WEB-FR'}
              options={Object.keys(WEB_MENU).map((key: any) => {
                return { value: key, label: `${WEB_MENU[key as WebMenuKeys]}` };
              })}
              onChange={handleSelectWebMenu}
              disabled
            />
            </DropdownWrapper>
          )}
          <HorizontalLine />
          <H2>Copy Menu</H2>
          <InputWrapper>
          <DropdownWrapper>
          <Autocomplete 
            disabled={!storeNoMatch}
            label='Source Store'
            id='sourceStore' 
            options={selectOptions}
            onInputChange={handleInputChange} 
            onChange={handleSourceStoreNumber}
            noOptionsMessage={'Please type a valid Store Number'}
          />
          </DropdownWrapper>
          <CopyMenuWrapper>
            <Dropdown 
            label='Source Menu' 
            disabled={!storeNoMatch}
             id='sourceMenu' 
             options={sourceMenuOptions}
             onChange={handleSelectMenu}
              />
          </CopyMenuWrapper>
          <InputWrapperThree>
            <Input
              label='Set Effective Date'
              id='effectiveDate'
              type='date'
              min={todays_date.toISOString().slice(0, 10)}
              value={formValues?.effectiveDate}
              onChange={handleEffectiveDateChange}
              disabled={!storeNoMatch}
            />
          </InputWrapperThree>

          </InputWrapper>
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSave} disabled={!checkFormFields()} isLoading={isLoading} />
        </Buttons>
      </Form>
    </>
  );
};

export default AddStoreRecord;
